/**
 * Wizard - 5-step tire filter state machine with UI rendering.
 *
 * Bu surum, adim degerlerini Shopify'in collection.filters yanitindan
 * (TireFilterStore araciligiyla) cekerek "cascading" filtre davranisi sunar.
 * Her adimda yalniz onceki secimlere uygun (count > 0) degerler gosterilir.
 */

class TireWizard {
  constructor({ filterBarId, popoverId, diagram, store, getSeason, onComplete, onStepChange }) {
    this.filterBar = document.getElementById(filterBarId);
    this.popover = document.getElementById(popoverId);
    this.diagram = diagram;
    this.store = store || null;
    this.getSeason = typeof getSeason === 'function' ? getSeason : () => null;
    this.onComplete = onComplete;
    this.onStepChange = onStepChange;

    this.currentStep = 0;
    this.totalSteps = TIRE_DATA.steps.length;
    this.selections = {};
    this.isOpen = false;
    this._renderToken = 0;

    this._buildFilterBar();
    this._buildPopover();
    this._bindEvents();
    this._setDefaults();
  }

  /**
   * Varsayılan lastik ölçülerini ayarlar: 205/55/16
   * Sayfa ilk açıldığında filter bar ve diagram bu değerlerle başlar.
   */
  _setDefaults() {
    const defaults = { width: '205', height: '55', inch: '16' };
    
    Object.entries(defaults).forEach(([key, value]) => {
      this.selections[key] = value;
      
      const valEl = document.getElementById(`val-${key}`);
      if (valEl) valEl.textContent = value;
      
      const segEl = document.getElementById(`seg-${key}`);
      if (segEl) segEl.classList.add('filled');
      
      if (this.diagram && typeof this.diagram.updateValue === 'function') {
        this.diagram.updateValue(key, value);
      }
    });
    
    // Varsayılan dolu alanlar olduğu için wizard açıldığında ilk boş adımdan başla
    this.currentStep = 3; // load adımı (ilk boş alan)
  }

  _buildFilterBar() {
    
    this.filterBar.innerHTML = TIRE_DATA.steps.map((step, i) => `
      <button class="filter-segment" data-step="${i}" id="seg-${step.key}">
        <span class="segment-label">${step.label === 'Width' ? 'Breite' : step.label === 'Height' ? 'Höhe' : step.label === 'Inch' ? 'Zoll' : step.label === 'Load' ? 'Last' : step.label === 'Speed' ? 'Speed' : step.label}</span>
        <span class="segment-value" id="val-${step.key}">—</span>
      </button>
    `).join('<span class="segment-divider"></span>');
  }

  _buildPopover() {
    const stepIndex = this.currentStep;
    this.popover.innerHTML = `
      <div class="popover-inner">
        <div class="popover-header">
          <button class="popover-back" id="wizardBack" aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M13 16L7 10L13 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <h3 class="popover-title" id="wizardTitle">Reifenbreite angeben</h3>
          <div class="stepper" id="wizardStepper"></div>
        </div>
        <div class="popover-body">
          <div class="popover-content-area">
            <div class="value-grid value-grid-${stepIndex}" id="wizardGrid"></div>
            <div class="diagram-container" id="diagramContainer"></div>
          </div>
          <p class="popover-hint" id="wizardHint"></p>
        </div>
      </div>
    `;

    const diagramContainer = document.getElementById('diagramContainer');
    let diagramEl = document.getElementById('tireDiagram');

    if (diagramContainer) {
      if (!diagramEl) {
        diagramEl = document.createElement('div');
        diagramEl.id = 'tireDiagram';
      }

      diagramContainer.appendChild(diagramEl);
      diagramEl.style.display = 'block';

      // If diagram markup was lost for any reason, recreate it safely.
      if (this.diagram && typeof this.diagram.render === 'function') {
        this.diagram.container = diagramEl;
        this.diagram.render();
      }
    }

    this._buildStepper();
  }

  _buildStepper() {
    const stepper = document.getElementById('wizardStepper');
    stepper.innerHTML = TIRE_DATA.steps.map((_, i) => `
      <span class="stepper-dot ${i === 0 ? 'active' : ''}" data-step="${i}"></span>
    `).join('<span class="stepper-line"></span>');
  }

  _bindEvents() {
    this.filterBar.addEventListener('click', (e) => {
      const seg = e.target.closest('.filter-segment');
      if (seg) {
        const step = parseInt(seg.dataset.step);
        this.goToStep(step);
        this.open();
      }
    });

    document.getElementById('wizardBack').addEventListener('click', () => {
      if (this.currentStep > 0) {
        this.goToStep(this.currentStep - 1);
      } else {
        this.close();
      }
    });

    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.popover.contains(e.target) && !this.filterBar.contains(e.target)) {
        this.close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  open() {
    this.isOpen = true;
    this.popover.classList.add('open');
    this.filterBar.classList.add('active');
    this._renderStep();
  }

  close() {
    this.isOpen = false;
    this.popover.classList.remove('open');
    this.filterBar.classList.remove('active');
  }

  goToStep(step) {
    this.currentStep = Math.max(0, Math.min(step, this.totalSteps - 1));
    if (this.isOpen) {
      this._renderStep();
    }
    if (this.onStepChange) {
      this.onStepChange(this.currentStep);
    }
  }

  /**
   * Aktif adimi yeniden cizer. Season degisimi ya da seleciton gun
   * geldiginde disaridan cagrilabilir.
   */
  refreshCurrentStep() {
    if (this.isOpen) this._renderStep();
  }

  async _renderStep() {
    const step = TIRE_DATA.steps[this.currentStep];
    const title = document.getElementById('wizardTitle');
    const grid = document.getElementById('wizardGrid');
    const hint = document.getElementById('wizardHint');

    grid.className = `value-grid value-grid-${step.key}`;

    switch (step.key) {
      case 'width':
        title.textContent = 'Reifenbreite angeben';
        break;
      case 'height':
        title.textContent = 'Reifenhöhe angeben';
        break;
      case 'inch':
        title.textContent = 'Reifengröße angeben';
        break;
      case 'load':
        title.textContent = 'Lastindex angeben';
        break;
      case 'speed':
        title.textContent = 'Geschwindigkeitsindex angeben';
        break;
    }
    // title.textContent = `Select ${step.label}`;
    this._updateStepper();
    this.diagram.setActiveStep(this.currentStep);

    const hints = [
      'Überprüfen Sie die Reifengröße auf Ihren aktuellen Reifen.',
      'Das Querschnittsverhältnis wird nach dem Schrägstrich angegeben.',
      'Felgendurchmesser in Zoll – auf der Felge gekennzeichnet.',
      'Der Lastindex (Tragfähigkeitsindex) muss mindestens dem der Originalbereifung entsprechen. (Empfohlen)',
      'Der Geschwindigkeitsindex muss dem Original entsprechen oder diesen übertreffen. (Empfohlen)',
    ];
    hint.textContent = hints[this.currentStep] || '';

    grid.innerHTML = '<div class="value-grid__loading">Loading…</div>';

    const token = ++this._renderToken;
    let items = [];
    try {
      items = await this._getStepValues(this.currentStep);
    } catch (err) {
      console.error('[TireWizard] step values fetch failed', err);
      items = [];
    }

    if (token !== this._renderToken) return;

    const selectedValue = this.selections[step.key];

    if (!items.length) {
      console.warn('[TireWizard] empty value list', {
        step: step.key,
        selections: { ...this.selections },
        season: this.getSeason && this.getSeason(),
      });
      grid.innerHTML = `
        <div class="value-grid__empty">
          <p>No matching tires for this combination.</p>
          ${this.currentStep > 0 ? '<button type="button" class="value-grid__back-btn" data-action="back">Go back</button>' : ''}
        </div>
      `;
      const backBtn = grid.querySelector('[data-action="back"]');
      if (backBtn) backBtn.addEventListener('click', () => this.goToStep(this.currentStep - 1));
      return;
    }

    grid.innerHTML = items.map((item) => {
      const val = String(item.value);
      const label = this._buildItemLabel(item, this.currentStep);
      const isSelected = selectedValue !== undefined && String(selectedValue) === val;
      const countAttr = item.count != null ? ` data-count="${item.count}"` : '';
      return `<button class="value-btn ${isSelected ? 'selected' : ''}" data-value="${this._escapeAttr(val)}"${countAttr}>${label}</button>`;
    }).join('');

    grid.querySelectorAll('.value-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); // Dış tıklama kapanmasını engelle
        this._selectValue(btn.dataset.value);
      });
    });
  }

  /**
   * Aktif adim icin server'dan ya da fallback static listeden degerleri uretir.
   * Donus formati: [{ value, label, count, active }]
   */
  async _getStepValues(stepIndex) {
    const step = TIRE_DATA.steps[stepIndex];
    const season = this.getSeason && this.getSeason();

    if (this.store) {
      const list = await this.store.getValues(step.key, this.selections, season);
      if (Array.isArray(list) && list.length > 0) {
        return list.map((v) => ({
          value: v.value,
          label: v.label,
          count: v.count,
          active: v.active,
        }));
      }
      return [];
    }

    return this._fallbackValues(stepIndex);
  }

  _fallbackValues(stepIndex) {
    const toItem = (raw) => {
      if (raw && typeof raw === 'object') {
        return { value: String(raw.code), label: String(raw.code), count: null, active: false };
      }
      return { value: String(raw), label: String(raw), count: null, active: false };
    };
    switch (stepIndex) {
      case 0: return TIRE_DATA.widths.map(toItem);
      case 1: return TIRE_DATA.heights.map(toItem);
      case 2: return TIRE_DATA.inches.map(toItem);
      case 3: return TIRE_DATA.loadIndices.map(toItem);
      case 4: return TIRE_DATA.speedIndices.map(toItem);
      default: return [];
    }
  }

  _buildItemLabel(item, stepIndex) {
    const code = String(item.value);
    if (stepIndex === 3) {
      const meta = (TIRE_DATA.loadIndices || []).find((l) => String(l.code) === code);
      return meta ? `${code}: bis ${meta.maxKg} kg` : code;
    }
    if (stepIndex === 4) {
      const meta = (TIRE_DATA.speedIndices || []).find((s) => String(s.code) === code);
      return meta ? `${code}: bis ${meta.maxKmh} km/h` : code;
    }
    return item.label || code;
  }

  _escapeAttr(str) {
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  _selectValue(rawValue) {
    const step = TIRE_DATA.steps[this.currentStep];

    if (this.selections[step.key] !== rawValue) {
      this._clearStepsAfter(this.currentStep);
    }

    this.selections[step.key] = rawValue;

    const valEl = document.getElementById(`val-${step.key}`);
    if (valEl) valEl.textContent = rawValue;

    const segEl = document.getElementById(`seg-${step.key}`);
    if (segEl) segEl.classList.add('filled');

    this.diagram.updateValue(step.key, rawValue);

    if (this.currentStep < this.totalSteps - 1) {
      const next = this.currentStep + 1;
      this._renderStep();
      setTimeout(() => this.goToStep(next), 200);
    } else {
      setTimeout(() => {
        this.close();
        if (this.onComplete) this.onComplete(this.selections);
      }, 300);
    }
  }

  /**
   * Bir adimdaki secim degistiginde, ondan SONRAKI tum adimlarin secimlerini
   * gecersiz sayar ve bar etiketlerini sifirlar. Cascading filtreler icin sart.
   */
  _clearStepsAfter(stepIndex) {
    for (let i = stepIndex + 1; i < this.totalSteps; i++) {
      const k = TIRE_DATA.steps[i].key;
      if (this.selections[k] !== undefined) {
        delete this.selections[k];
      }
      const valEl = document.getElementById(`val-${k}`);
      if (valEl) valEl.textContent = '—';
      const segEl = document.getElementById(`seg-${k}`);
      if (segEl) segEl.classList.remove('filled');
      if (this.diagram && typeof this.diagram.updateValue === 'function') {
        this.diagram.updateValue(k, '—');
      }
    }

    const findBtn = document.getElementById('findBtn');
    if (findBtn) findBtn.classList.remove('ready');
  }

  _updateStepper() {
    const dots = document.querySelectorAll('.stepper-dot');
    const lines = document.querySelectorAll('.stepper-line');

    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i <= this.currentStep);
      dot.classList.toggle('completed', this.selections[TIRE_DATA.steps[i].key] !== undefined);
    });
    lines.forEach((line, i) => {
      line.classList.toggle('filled', i < this.currentStep);
    });
  }

  getSelections() {
    return { ...this.selections };
  }

  reset() {
    this.selections = {};
    this.currentStep = 0;
    TIRE_DATA.steps.forEach((step) => {
      const valEl = document.getElementById(`val-${step.key}`);
      if (valEl) valEl.textContent = '—';
      const segEl = document.getElementById(`seg-${step.key}`);
      if (segEl) segEl.classList.remove('filled');
    });
    if (this.diagram && typeof this.diagram.reset === 'function') this.diagram.reset();
    const findBtn = document.getElementById('findBtn');
    if (findBtn) findBtn.classList.remove('ready');
    this.close();
    // Reset sonrası varsayılan değerleri tekrar yükle
    this._setDefaults();
  }
}
